import { axiosInstance } from '~/api/base'
import {
  buildNgmHeaders,
  NGM_API_BASE_PATH,
  NgmClusterContext
} from '~/api/ngm'

import type {
  GetStatementV2FieldValuesParams,
  GetStatementV2ListParams,
  GetStatementV2PlanDetailParams,
  GetStatementV2PlansParams,
  GetStatementV2SummaryParams,
  GetStatementV2TopStatsParams,
  StatementV2ListResponse,
  StatementV2Model,
  StatementV2SummaryEnvelope,
  StatementV2TopStatsEnvelope
} from './types'

type StatementV2QueryValue =
  | string
  | number
  | boolean
  | readonly string[]
  | undefined
  | null

export function statementV2ContextQueryKey(ctx?: NgmClusterContext | null) {
  return [
    ctx?.orgExternalId || '',
    ctx?.projectId || '',
    ctx?.provider || '',
    ctx?.region || '',
    ctx?.deployType || '',
    ctx?.clusterId || ''
  ] as const
}

/** Serialize arrays as repeated query keys (`plans=a&plans=b`). */
export function serializeStatementV2Params(
  params: Record<string, StatementV2QueryValue>
): string {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item))
      return
    }

    searchParams.set(key, String(value))
  })

  return searchParams.toString()
}

function getStatementV2<T>(
  ctx: NgmClusterContext,
  path: string,
  params: object,
  signal?: AbortSignal
): Promise<T> {
  const query = serializeStatementV2Params({
    ...(params as Record<string, StatementV2QueryValue>),
    data_source: 'v2'
  })

  return axiosInstance
    .get<T>(`${NGM_API_BASE_PATH}${path}?${query}`, {
      headers: buildNgmHeaders(ctx),
      signal,
      // These pages render their own retry/error state.
      skipErrorHandler: true
    })
    .then((response) => response.data)
}

export function getStatementV2FieldValues(
  ctx: NgmClusterContext,
  params: GetStatementV2FieldValuesParams,
  signal?: AbortSignal
) {
  return getStatementV2<string[]>(
    ctx,
    '/statements/field_values',
    params,
    signal
  )
}

export function getStatementV2List(
  ctx: NgmClusterContext,
  params: GetStatementV2ListParams,
  signal?: AbortSignal
) {
  return getStatementV2<StatementV2ListResponse>(
    ctx,
    '/statements/list',
    params,
    signal
  )
}

export function getStatementV2Summary(
  ctx: NgmClusterContext,
  params: GetStatementV2SummaryParams,
  signal?: AbortSignal
) {
  return getStatementV2<StatementV2SummaryEnvelope>(
    ctx,
    '/statements/summary',
    params,
    signal
  )
}

export function getStatementV2TopStats(
  ctx: NgmClusterContext,
  params: GetStatementV2TopStatsParams,
  signal?: AbortSignal
) {
  return getStatementV2<StatementV2TopStatsEnvelope>(
    ctx,
    '/statements/topstats',
    params,
    signal
  )
}

export function getStatementV2Plans(
  ctx: NgmClusterContext,
  params: GetStatementV2PlansParams,
  signal?: AbortSignal
) {
  return getStatementV2<StatementV2Model[]>(
    ctx,
    '/statements/plans',
    params,
    signal
  )
}

export function getStatementV2PlanDetail(
  ctx: NgmClusterContext,
  params: GetStatementV2PlanDetailParams,
  signal?: AbortSignal
) {
  return getStatementV2<StatementV2Model>(
    ctx,
    '/statements/plan/detail',
    params,
    signal
  )
}
